/**
 * Standard Ballot Initiatives report (PRD v2 §5.17) — the Candidates report's
 * shape with two deliberate differences: card view only, and an election-cycle
 * selector, which a measure report cannot do without.
 *
 *   My   workspace: measures tracked in this workspace (with its position,
 *        hot flag, priority and notes); organization: the union across the
 *        org's workspaces, deduplicated, badged per workspace.
 *   All  every published, ballot-visible measure of the selected cycle.
 *
 * Cards render in a responsive grid grouped by jurisdiction level (Statewide
 * first) and carry the organization's filled-in custom fields.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Vote } from 'lucide-react';
import api from '../../lib/api';
import { useWorkspaceReports } from '../../hooks/useWorkspaceReports';
import ReportLogoHeader from './ReportLogoHeader';
import ExportMenu from '../ExportMenu';
import SegmentedControl from '../SegmentedControl';
import EmptyState from '../EmptyState';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import { HelpTrigger } from '../../help';
import InitiativeReportCard from '../ballot-initiatives/InitiativeReportCard';
import { collectCustomFieldColumns, normalizeCustomFieldValues } from '../candidates/candidateCustomFieldUtils';
import { currentDatasetInitiativeCycle, normalizeInitiativeCycles } from '../ballot-initiatives/initiativeDisplay';
import { isBallotVisible, jurisdictionLabel } from '../../constants/ballotInitiatives';
import type { InitiativeReportRow } from '../ballot-initiatives/types';

export type InitiativeReportSource = 'my' | 'all';

interface BallotInitiativesReportProps {
  workspaceId?: string;
  workspaceName?: string;
  organizationId?: string;
  organizationName?: string;
  /** Public share page: toggle hidden, no exports, rows supplied by the parent. */
  isPublicView?: boolean;
  prefetchedInitiatives?: InitiativeReportRow[];
}

/** Snake_case custom field values from the API become the camelCase shape the display helpers read. */
export function normalizeInitiativeReportRow(raw: InitiativeReportRow): InitiativeReportRow {
  return { ...raw, customFieldValues: normalizeCustomFieldValues(raw.custom_field_values) };
}

/** Statewide first, then the other levels in the order they appear. */
export function groupInitiativesByJurisdiction(rows: InitiativeReportRow[]): Array<[string, InitiativeReportRow[]]> {
  const groups = new Map<string, InitiativeReportRow[]>();
  for (const row of rows) {
    const key = row.jurisdiction_level || 'other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }
  return Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === b) return 0;
    if (a === 'statewide') return -1;
    if (b === 'statewide') return 1;
    return 0;
  });
}

const BallotInitiativesReport: React.FC<BallotInitiativesReportProps> = ({
  workspaceId,
  workspaceName,
  organizationId,
  organizationName,
  isPublicView = false,
  prefetchedInitiatives,
}) => {
  const isOrgMode = !!organizationId;
  const hasPrefetched = Array.isArray(prefetchedInitiatives);
  const [source, setSource] = useState<InitiativeReportSource>('my');
  const [cycle, setCycle] = useState<string>('');
  const { exportReport } = useWorkspaceReports(workspaceId, organizationId);

  // Cycles and the configured current cycle — the same call the Pro directory makes.
  const { data: cycleOptions, isFetched: cyclesFetched } = useQuery({
    queryKey: ['ballot-initiatives', 'filter-options'],
    enabled: !hasPrefetched,
    queryFn: async () => {
      const response = await api.get('/ballot-initiatives/filter-options');
      const data = response.data.data ?? {};
      const cycles = normalizeInitiativeCycles(Array.isArray(data.initiative_cycles) ? data.initiative_cycles : []);
      return { cycles, currentCycle: currentDatasetInitiativeCycle(cycles, data.current_cycle) };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Default to the current cycle once, as the directory does; the reader may change it after.
  const cycleDefaultApplied = useRef(false);
  useEffect(() => {
    if (cycleDefaultApplied.current || !cyclesFetched || !cycleOptions) return;
    cycleDefaultApplied.current = true;
    setCycle((current) => current || cycleOptions.currentCycle || cycleOptions.cycles[0] || '');
  }, [cycleOptions, cyclesFetched]);

  // My — workspace tracking, or the org union.
  const myQuery = useQuery({
    queryKey: isOrgMode ? ['org-ballot-initiatives', organizationId] : ['workspace-ballot-initiatives', workspaceId],
    enabled: !hasPrefetched && source === 'my' && Boolean(isOrgMode ? organizationId : workspaceId),
    queryFn: async () => {
      const url = isOrgMode
        ? `/org-reports/${organizationId}/ballot-initiatives`
        : `/ballot-initiatives/workspace/${workspaceId}/tracking`;
      const response = await api.get(url);
      return ((response.data.data ?? []) as InitiativeReportRow[]).map(normalizeInitiativeReportRow);
    },
    staleTime: 30000,
  });

  // All — the cycle's published, on-ballot measures. Workspace scope carries
  // the workspace's custom fields; the org has no workspace to read them from.
  const allQuery = useQuery({
    queryKey: ['ballot-initiatives-report-all', isOrgMode ? organizationId : workspaceId, cycle],
    enabled: !hasPrefetched && source === 'all' && Boolean(cycle),
    queryFn: async () => {
      const response = isOrgMode
        ? await api.get('/ballot-initiatives', {
          params: { initiative_cycle: cycle, status_group: 'ballot_visible', publication_status: 'published', limit: 200 },
        })
        : await api.get(`/workspace-reports/${workspaceId}/ballot-initiatives/all`, { params: { initiative_cycle: cycle } });
      return ((response.data.data ?? []) as InitiativeReportRow[]).map(normalizeInitiativeReportRow);
    },
    staleTime: 30000,
  });

  const rows = useMemo((): InitiativeReportRow[] => {
    if (hasPrefetched) return prefetchedInitiatives!.map(normalizeInitiativeReportRow);
    const data = (source === 'my' ? myQuery.data : allQuery.data) ?? [];
    // The cycle scopes both tabs, and the report is the ballot: a tracked
    // measure from another cycle, or one still working toward the ballot,
    // is out of this run (Angie, 2026-09-13).
    return data.filter((row) => isBallotVisible(row.status) && (!cycle || !row.initiative_cycle || row.initiative_cycle === cycle));
  }, [hasPrefetched, prefetchedInitiatives, source, myQuery.data, allQuery.data, cycle]);

  const isLoading = !hasPrefetched && (source === 'my' ? myQuery.isLoading : allQuery.isLoading || !cycle);
  const error = source === 'my' ? myQuery.error : allQuery.error;

  const customFieldColumns = useMemo(() => collectCustomFieldColumns(rows), [rows]);
  const groups = useMemo(() => groupInitiativesByJurisdiction(rows), [rows]);

  const handleExport = useCallback(async (format: string) => {
    try {
      await exportReport('ballot-initiatives', format as 'pdf', { viewMode: 'card', initiativeCycle: cycle, source });
    } catch (err) {
      console.error('Export error:', err);
    }
  }, [exportReport, cycle, source]);

  const scopeName = isOrgMode ? organizationName : workspaceName;

  return (
    <div className="p-3 sm:p-6">
      <ReportLogoHeader workspaceId={workspaceId} />

      {/* The public share page already titles the report and names the
          organization above this component; repeating them inside a second
          card was most of the nesting on a phone (Angie, 2026-09-13). */}
      {!isPublicView && (
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-left text-text-primary dark:text-text-dark-primary flex items-center gap-2">
          <Vote className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          Ballot Initiatives Report
          {!isPublicView && <HelpTrigger contentKey="report-ballot-initiatives" size="sm" />}
        </h3>
        <p className="text-sm text-left text-text-secondary dark:text-text-dark-secondary mt-1">
          {source === 'my'
            ? (isOrgMode ? 'Measures tracked across all workspaces' : 'Measures tracked in this workspace')
            : 'Every published measure on the ballot'}
          {cycle ? ` for the ${cycle} cycle` : ''}, as citizens see them
          {scopeName ? ` · ${scopeName}` : ''}
        </p>
      </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {!isPublicView && (
            <SegmentedControl<InitiativeReportSource>
              ariaLabel="Which measures"
              value={source}
              onChange={setSource}
              options={[
                { id: 'my', label: 'My Ballot Initiatives' },
                { id: 'all', label: 'All Ballot Initiatives' },
              ]}
            />
          )}
          {!hasPrefetched && (
            <label className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
              <span>Cycle</span>
              <select
                className="input py-1.5 text-sm"
                value={cycle}
                onChange={(e) => setCycle(e.target.value)}
                aria-label="Election cycle"
              >
                {(cycleOptions?.cycles ?? (cycle ? [cycle] : [])).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          )}
          <div className="flex items-center gap-2">
            <Vote className="w-4 h-4 text-text-tertiary" />
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {rows.length} {source === 'my' ? 'tracked' : 'total'} {rows.length === 1 ? 'measure' : 'measures'}
            </span>
          </div>
        </div>

        {!isPublicView && (
          <ExportMenu formats={['pdf']} onExport={(format) => handleExport(format)} disabled={rows.length === 0} />
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <EmptyState
          title="The report could not be loaded"
          hint="Try again in a moment. If it keeps failing, the workspace may no longer be accessible."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Vote}
          title={source === 'my' ? 'No Tracked Ballot Initiatives' : 'No Ballot Initiatives Found'}
          hint={source === 'my'
            ? (isOrgMode
              ? 'No measures are being tracked in any workspace in this organization for this cycle.'
              : 'Track measures from the Ballot Initiatives directory to see them here.')
            : 'No published measures are on the ballot for this cycle yet.'}
        />
      ) : (
        <div className="space-y-6">
          {groups.map(([level, group]) => (
            <section key={level}>
              {groups.length > 1 && (
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
                  {jurisdictionLabel(level)} ({group.length})
                </h4>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {group.map((row) => (
                  <InitiativeReportCard
                    key={row.id}
                    initiative={row}
                    showTracking={source === 'my' && !isPublicView}
                    customFieldColumns={customFieldColumns}
                    isPublicView={isPublicView}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default BallotInitiativesReport;
